import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import logo from "../img/logo.svg";
import "../styles/SignUp.scss";
import { Helmet } from "react-helmet";
import { getCanonicalUrl } from "../utils/seoConfig";
import emailjs from "@emailjs/browser";
import { validateEmail } from "../utils/ValidateUtils";
import Toast from "../Components/Toast";

function SignUp() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    useCase: "business",
    company: "",
    jobFunction: "",
    country: "",
    termsAccepted: false,
    message: "",
  });

  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAIL_JS_PUBLIC_KEY;
    if (!publicKey) {
      console.error("EmailJS public key is missing!");
      return;
    }
    console.log("Initializing EmailJS with public key:", publicKey);
    emailjs.init(publicKey);
  }, []);

  const handleCloseToast = () => {
    setToast({ ...toast, show: false });
  };

  const handleSubmit = async (e) => {
    if (!validateEmail(formData.email)) {
      setToast({
        show: true,
        message: t("InvalidEmail"),
        type: "error",
      });
      return;
    }

    e.preventDefault();
    setIsLoading(true);

    const serviceId = process.env.REACT_APP_EMAIL_JS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAIL_JS_TEMPLATE_ID;
    try {
      const emailBody = `
        Nova registracija sa sajta:
        
        Email: ${formData.email}
        Name: ${formData.firstName}
        Lastname: ${formData.lastName}
        Using product for: ${
          formData.useCase === "business" ? "Business" : "Other"
        }
        Company: ${formData.company}
        Job function: ${formData.jobFunction}
        Country: ${formData.country}
        Message: ${formData.message}
      `;

      await emailjs.send(serviceId, templateId, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        message: emailBody,
        company: formData.company,
      });

      setToast({
        show: true,
        message: t("ThankYouForRegistering"),
        type: "success",
      });
    } catch (error) {
      console.error("Error sending email:", error);
      setToast({
        show: true,
        message: t("ErrorSendingForm"),
        type: "error",
      });
    } finally {
      setIsLoading(false);
      setFormData({
        email: "",
        firstName: "",
        lastName: "",
        useCase: "business",
        company: "",
        jobFunction: "",
        country: "",
        termsAccepted: false,
        message: "",
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  return (
    <>
      <Helmet>
        <title>{t("RequestDemo")} - Predsense</title>
        <link rel="canonical" href={getCanonicalUrl("/signup")} />
      </Helmet>
      {toast.show && (
        <div className="toast-container">
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={handleCloseToast}
          />
        </div>
      )}
      <div className="signup-container">
        <div className="signup-left">
          <div className="left-content">
            <h1>{t("PredictiveMaintenance")}</h1>
            <p className="fix-that-text">{t("FixThat")}</p>

            <div className="feature-list">
              <div className="feature-item">
                <svg viewBox="0 0 24 24" className="check-icon">
                  <path
                    fill="currentColor"
                    d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"
                  />
                </svg>
                <span>{t("ReduceMaintenance")}</span>
              </div>
              <div className="feature-item">
                <svg viewBox="0 0 24 24" className="check-icon">
                  <path
                    fill="currentColor"
                    d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"
                  />
                </svg>
                <span>{t("PreventMachine")}</span>
              </div>
              <div className="feature-item">
                <svg viewBox="0 0 24 24" className="check-icon">
                  <path
                    fill="currentColor"
                    d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"
                  />
                </svg>
                <span>{t("StartImplementing")}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="signup-right">
          <div className="form-container">
            {isLoading && (
              <div className="loader-overlay">
                <div className="loader"></div>
              </div>
            )}
            <div className="form-header">
              <img src={logo} alt="Predsense Logo" className="logo" />
              <h2>{t("CreateFreeAccount")}</h2>
              <p>
                {t("AlreadyHaveAccount")}{" "}
                <Link to="https://app.predsense.ai">{t("SignInHere")}</Link>
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">{t("EmailAddress")} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t("EnterWorkEmail")}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">{t("FirstName")} *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder={t("EnterFirstName")}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">{t("LastName")} *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder={t("EnterLastName")}
                  />
                </div>
              </div>

              <div className="form-group radio-group">
                <label>{t("WhatWillYouUse")}</label>
                <div className="radio-options">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="useCase"
                      value="business"
                      checked={formData.useCase === "business"}
                      onChange={handleChange}
                    />
                    <span>{t("BusinessUse")}</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="useCase"
                      value="other"
                      checked={formData.useCase === "other"}
                      onChange={handleChange}
                    />
                    <span>{t("Other")}</span>
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">{t("CompanyName")} *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    placeholder={t("EnterCompanyName")}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="jobFunction">{t("JobFunction")}</label>
                  <input
                    type="text"
                    id="jobFunction"
                    name="jobFunction"
                    value={formData.jobFunction}
                    onChange={handleChange}
                    placeholder={t("EnterJobFunction")}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="country">{t("Country")} *</label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  className="country-select"
                >
                  <option value="">{t("SelectCountry")}</option>
                  <option value="Austria">Austria</option>
                  <option value="Croatia">Croatia</option>
                  <option value="Czech Republic">Czech Republic</option>
                  <option value="Germany">Germany</option>
                  <option value="Hungary">Hungary</option>
                  <option value="Italy">Italy</option>
                  <option value="Poland">Poland</option>
                  <option value="Slovakia">Slovakia</option>
                  <option value="Slovenia">Slovenia</option>
                  <option value="Switzerland">Switzerland</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">{t("Message")}</label>
                <div className="textarea-container">
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    maxLength={1000}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t("TellUsAboutYourNeeds")}
                  />
                  <div className="char-counter">
                    {formData.message.length} / 1000
                  </div>
                </div>
              </div>

              <div className="form-group checkbox">
                <input
                  type="checkbox"
                  id="terms"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="terms">
                  {t("IAgreeToThe")} <a href="/terms">{t("EndUserLicense")}</a>{" "}
                  {t("AndThe")}{" "}
                  <a href="/privacy-policy">{t("PrivacyPolicy")}</a> *
                </label>
              </div>

              <button type="submit" className="submit-button">
                {t("GetPredsenseTrial")} →
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default SignUp;
